import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import type { AppConfig, Entity, FilterBy } from '../types'
import type { Action, BookingState } from '../booking/state'
import { isTelemed, regionSkipped } from '../booking/state'
import {
  buildSelection,
  createAvailabilitySession,
  firstAvailOf,
  selectionKey,
  type FirstSlot
} from '../booking/availability'
import { track } from '../analytics'
import {
  todayCentral,
  nextNonSunday,
  formatDateShort,
  dateHeaderParts,
  formatTime12,
  centralTzAbbrev
} from '../dates'

// Capped at 5, not 7: the widget itself is never wider than --maxw (760px),
// and the identity rail takes ~200px of every card row, so a 7th column only
// squeezes the times into two lines no matter how wide the screen is.
const DATE_COUNT_MQ = '(min-width: 600px)'
/** date_count within the allowed 3–7 (§6.1), responsive to viewport. */
function pickDateCount(): number {
  return window.matchMedia(DATE_COUNT_MQ).matches ? 5 : 3
}

/** 7.5 Fetch & choose an available slot. */
export function SlotPicker({
  config,
  state,
  dispatch,
  onBack,
  providerBased,
  embedded
}: {
  config: AppConfig
  state: BookingState
  dispatch: React.Dispatch<Action>
  onBack: () => void
  providerBased?: boolean
  embedded?: boolean
}) {
  const heading = config.copy?.slotHeading || (providerBased ? 'Choose your provider' : 'Choose a time')
  // For provider-based scheduling (behavioral health) each result is a clinician;
  // the visit-type badge on the card reflects Virtual vs In-Person.
  const visitBadge = isTelemed(state.profile, state.subProfile) ? 'Virtual' : 'In-Person'
  // Retain the chosen date window across region switches / edits (§3.3).
  const [start, setStart] = useState<string>(() => state.slotStart ?? nextNonSunday(todayCentral()))
  const [prevStack, setPrevStack] = useState<string[]>([])
  const tz = useMemo(centralTzAbbrev, [])
  const [filterBy, setFilterBy] = useState<FilterBy | null>(null)

  // Columns follow the viewport (3 below 600px, else 5); crossing the
  // breakpoint refetches the window at the new date_count.
  const [dateCount, setDateCount] = useState(pickDateCount)
  useEffect(() => {
    const mq = window.matchMedia(DATE_COUNT_MQ)
    const onChange = () => setDateCount(mq.matches ? 5 : 3)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // The availability session (booking/availability.ts) owns fetching, the
  // abort/race handling, and the today-anchored "First available" cache. A
  // changed selection (service/region/zip/…) means a new session — that is
  // the cache invalidation. Paging (start alone) reuses the session.
  const selection = useMemo(
    () => buildSelection(state, !!providerBased),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selection is a pure projection of these fields
    [state.profile, state.subProfile, state.status, state.region, state.zip, state.coords, providerBased]
  )
  const sessionKey = selectionKey(selection)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionKey is derived from selection
  const session = useMemo(() => createAvailabilitySession(selection), [sessionKey])
  useEffect(() => () => session.dispose(), [session])
  const snap = useSyncExternalStore(session.subscribe, session.getState)
  useEffect(() => {
    session.load({ start, filterBy, dateCount })
  }, [session, start, filterBy, dateCount])

  const goNext = (nextDate: string) => {
    track('select_next_page', { from: start, to: nextDate })
    setPrevStack((s) => [...s, start])
    setStart(nextDate)
    dispatch({ type: 'SET_SLOT_START', date: nextDate })
  }

  const goPrev = () => {
    if (!prevStack.length) return
    track('select_previous_page')
    const stack = prevStack.slice()
    const prev = stack.pop()!
    setPrevStack(stack)
    setStart(prev)
    dispatch({ type: 'SET_SLOT_START', date: prev })
  }

  // Stable identity so memoized cards don't re-render on unrelated state.
  const onPick = useCallback(
    (entity: Entity, date: string, time: string) => {
      track('select_time', { office: entity.office_id, doctor: entity.doctor_id, date, time })
      dispatch({
        type: 'SELECT_SLOT',
        slot: { entity, office_id: entity.office_id, doctor_id: entity.doctor_id, date, time }
      })
    },
    [dispatch]
  )

  const nextDate = snap.phase === 'ready' ? snap.data!.next_date : null
  const loading = snap.phase === 'loading'
  // Keep the pinned controls stable across a page change: fall back to the
  // previous window's shape while the new one is in flight.
  const toolbarData = snap.phase === 'ready' ? snap.data : snap.lastReady
  // Every entity shares the same date window, but a "program not available"
  // clinic comes back with no dates — take the columns from the first entity
  // that actually has them.
  const headerDates = toolbarData?.entities.find((e) => e.dates.length > 0)?.dates ?? []

  // Time-of-day filter presented as one connected segmented control with a
  // sliding thumb (friendlier "Morning/Afternoon" labels; AM/PM under the hood).
  const timeOptions = [
    { value: null, label: 'All' },
    { value: 'AM', label: 'Morning' },
    { value: 'PM', label: 'Afternoon' }
  ] as const
  const activeTimeIdx = timeOptions.findIndex((o) => o.value === filterBy)

  return (
    <section>
      {/* Sticky toolbar (below the sticky app header): heading + time-of-day
          filter share one row to save vertical space, with date paging under
          them — all stay pinned while the location list scrolls. The Back
          control lives at the bottom (see the shared action row below) so it
          stays in the same place as every other step. */}
      <div className="slot-toolbar">
        <div className="slot-toolbar-head">
          <h1 className="step-heading">{heading}</h1>
          <div className="time-toggle" role="group" aria-label="Time of day">
            <span
              className="time-toggle-thumb"
              style={{ transform: `translateX(${Math.max(activeTimeIdx, 0) * 100}%)` }}
              aria-hidden
            />
            {timeOptions.map((o) => (
              <button
                key={o.label}
                type="button"
                className={`time-toggle-btn${filterBy === o.value ? ' active' : ''}`}
                onClick={() => setFilterBy(o.value)}
                aria-pressed={filterBy === o.value}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
        {toolbarData && toolbarData.entities.length > 0 && (
          <div className="pager">
            <button className="btn" onClick={goPrev} disabled={loading || prevStack.length === 0}>
              Previous
            </button>
            <span className="spacer" />
            <button
              className="btn btn-primary"
              onClick={() => nextDate && goNext(nextDate)}
              disabled={loading || !nextDate}
            >
              Next
            </button>
          </div>
        )}
        {/* One shared date header for every clinic card (all cards share the
            same window + column geometry); pinned with the toolbar. Provider
            cards keep per-card headers (their grids aren't rail-aligned).
            Slot buttons carry the date in their aria-label. */}
        {!providerBased && headerDates.length > 0 && (
          <div className="date-header-grid" aria-hidden>
            {headerDates.map((d) => {
              const { dow, md } = dateHeaderParts(d.date)
              return (
                <div className="date-col-header" key={d.date}>
                  <span className="dch-dow">{dow}</span>
                  <span className="dch-md">{md}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Selection context + timezone on one compact line — this sits between
          the pinned toolbar and the first card, so it must not eat height. */}
      <p className="slot-meta">
        {state.profile?.name}
        {state.subProfile ? ` · ${state.subProfile.name}` : ''}
        {/* A skipped region step auto-assigns a region for the API query — it
            isn't a location the user chose, so don't show it. */}
        {state.region && !regionSkipped(config, state.profile, state.subProfile)
          ? ` · ${state.region.name}`
          : ''}
        {state.zip ? ` · ${state.zip}` : ''}
        {` · all times Central (${tz})`}
      </p>

      {snap.phase === 'loading' && <SlotSkeleton dateCount={dateCount} />}

      {snap.phase === 'error' && (
        <div className="state-block" role="alert">
          <p className="error-text">We couldn’t load available times.</p>
          <button className="btn btn-primary" onClick={() => session.load({ start, filterBy, dateCount })}>
            Retry
          </button>
        </div>
      )}

      {snap.phase === 'ready' && snap.data!.entities.length === 0 && (
        <div className="state-block" role="status">
          <p>No availability for this selection.</p>
          <p>Try a different date, location, or service.</p>
        </div>
      )}

      {/* Announce refreshed results to screen readers without re-reading the list. */}
      <div className="sr-only" role="status">
        {snap.phase === 'ready' && snap.data!.entities.length > 0
          ? `Available times updated, ${snap.data!.entities.length} ${providerBased ? 'providers' : 'locations'} shown.`
          : ''}
      </div>

      {snap.phase === 'ready' &&
        snap.data!.entities.length > 0 &&
        snap.data!.entities.map((e, i) => (
          <EntityCard
            key={`${e.office_id}-${e.doctor_id}-${i}`}
            entity={e}
            onPick={onPick}
            providerBased={providerBased}
            visitBadge={visitBadge}
            firstAvailOverride={snap.firstAvail[`${e.office_id}-${e.doctor_id}`] ?? null}
          />
        ))}

      {/* Date paging is its own side-by-side control (kept out of the .btn-row so
          it doesn't stack/hoist on mobile). */}
      {snap.phase === 'ready' && (
        <div className="pager">
          <button className="btn" onClick={goPrev} disabled={prevStack.length === 0}>
            Previous
          </button>
          <span className="spacer" />
          <button
            className="btn btn-primary"
            onClick={() => nextDate && goNext(nextDate)}
            disabled={!nextDate}
          >
            Next
          </button>
        </div>
      )}

      {/* Back sits in the same spot as every other step: leading edge of the
          bottom .btn-row. */}
      {!embedded && (
        <div className="btn-row">
          <button className="btn btn-ghost" onClick={onBack}>
            ← Back
          </button>
        </div>
      )}
    </section>
  )
}

/** §3.2 keep the day columns compact: show at most this many times per day
 *  (the Morning/Afternoon filter reaches the rest). */
const MAX_TIMES_PER_DAY = 4

/** Memoized: cards re-render only when their entity/first-available change,
 *  not on every parent fetch-state transition. */
const EntityCard = memo(function EntityCard({
  entity,
  onPick,
  providerBased,
  visitBadge,
  firstAvailOverride
}: {
  entity: Entity
  onPick: (e: Entity, date: string, time: string) => void
  providerBased?: boolean
  visitBadge?: string
  firstAvailOverride?: FirstSlot | null
}) {
  const isProvider = entity.doctor_id != null
  const img = isProvider ? entity.doctor_url : entity.map_url
  const programNA = entity.message === 'program-not-available'

  // One-tap "First available": the today-anchored slot from the session when
  // known, else the earliest slot in the currently shown window.
  const firstAvail = firstAvailOverride ?? firstAvailOf(entity)

  return (
    <article className={`entity-card${providerBased ? ' provider' : ''}`}>
      <div className="entity-head">
        {img && (
          <img className={providerBased ? 'provider-photo' : ''} src={img} alt="" loading="lazy" />
        )}
        <div className="meta">
          {providerBased && visitBadge && <span className="visit-badge">{visitBadge}</span>}
          <div className="entity-name">
            {entity.name}
            {/* Clinic descriptor (cross streets) sits inline beside the name. */}
            {!providerBased && entity.description && (
              <span className="entity-desc">{entity.description}</span>
            )}
          </div>
          {/* Provider-based (behavioral health): show credentials + bio, not a clinic. */}
          {providerBased && entity.description && <div className="entity-sub">{entity.description}</div>}
          {/* No address/phone on this step — the details page's appointment
              summary carries the full location info. */}
          {providerBased && entity.wordpress_link && (
            <div className="entity-sub">
              <a href={entity.wordpress_link} target="_blank" rel="noreferrer">
                Learn more
              </a>
            </div>
          )}
          {entity.distance != null && <span className="entity-distance">{entity.distance.toFixed(1)} miles</span>}
        </div>
        {!programNA && firstAvail && (
          <button
            type="button"
            className="first-avail-btn"
            onClick={() => onPick(entity, firstAvail.date, firstAvail.time)}
            aria-label={`Book the first available appointment at ${entity.name}: ${formatDateShort(firstAvail.date)} ${formatTime12(firstAvail.time)}`}
          >
            <span className="fa-label">First available</span>
            {/* Separate spans (no "·") so the value wraps cleanly to two lines
                in the narrow mobile rail and stays on one line on desktop. */}
            <span className="fa-value">
              <span>{formatDateShort(firstAvail.date)}</span>{' '}
              <span>{formatTime12(firstAvail.time)}</span>
            </span>
          </button>
        )}
      </div>

      {programNA ? (
        <p className="message-banner" style={{ marginTop: 12 }}>
          This service isn’t available to book online for this provider right now. Please call the office above.
        </p>
      ) : (
        <div className="slot-grid">
          {entity.dates.map((d) => (
            <div className="slot-col" key={d.date}>
              {/* Clinic cards share the toolbar's pinned date header; provider
                  cards label each column themselves. */}
              {providerBased && <div className="date-col-header">{formatDateShort(d.date)}</div>}
              {d.times.length === 0 ? (
                <div className="slot-empty">—</div>
              ) : (
                d.times.slice(0, MAX_TIMES_PER_DAY).map((t) => (
                  <button
                    key={t}
                    className="slot-btn"
                    aria-label={`${formatDateShort(d.date)} ${formatTime12(t)}`}
                    onClick={() => onPick(entity, d.date, t)}
                  >
                    {formatTime12(t)}
                  </button>
                ))
              )}
            </div>
          ))}
        </div>
      )}
    </article>
  )
})

function SlotSkeleton({ dateCount }: { dateCount: number }) {
  return (
    <div className="entity-card" aria-hidden>
      <div className="entity-head">
        <div className="meta" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="skeleton" style={{ height: 16, width: '70%' }} />
          <div className="skeleton" style={{ height: 12, width: '50%' }} />
        </div>
      </div>
      <div className="slot-grid">
        {Array.from({ length: dateCount }).map((_, i) => (
          <div className="slot-col" key={i}>
            {Array.from({ length: MAX_TIMES_PER_DAY }).map((__, j) => (
              <div className="skeleton" key={j} style={{ height: 34 }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
